import { useState, useEffect, useCallback } from 'react';

/**
 * usePWA — detects install state, provides prompt trigger, detects browser/OS.
 *
 * Returns:
 *   canInstall    — true when the browser's beforeinstallprompt fired
 *   isInstalled   — true when running in standalone (PWA) mode
 *   isIOS         — true on iOS/iPadOS (needs manual Safari share-sheet)
 *   isAndroid     — true on Android
 *   isMobile      — true on any mobile device
 *   browser       — 'chrome' | 'edge' | 'firefox' | 'safari' | 'other'
 *   triggerInstall — call this to show the native prompt (Android/desktop)
 *   updateAvailable — true when a new service-worker version is waiting
 *   applyUpdate   — call this to reload with the new version
 */
export default function usePWA() {
  const [prompt, setPrompt]               = useState(null);
  const [canInstall, setCanInstall]       = useState(false);
  const [isInstalled, setIsInstalled]     = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  /* ── Detect environment ───────────────────────────────────── */
  const ua = navigator.userAgent || '';
  const isIOS      = /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid  = /Android/i.test(ua);
  const isMobile   = isIOS || isAndroid;
  const browser    = /Edg\//i.test(ua)    ? 'edge'
                   : /Chrome/i.test(ua)   ? 'chrome'
                   : /Firefox/i.test(ua)  ? 'firefox'
                   : /Safari/i.test(ua)   ? 'safari'
                   : 'other';

  /* ── Standalone / installed check ────────────────────────── */
  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    setIsInstalled(mq.matches || navigator.standalone === true);
    const handler = (e) => setIsInstalled(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  /* ── beforeinstallprompt ──────────────────────────────────── */
  useEffect(() => {
    // Reuse prompt stored by index.js if it fired before this hook mounted
    if (window.__pwaInstallPrompt) {
      setPrompt(window.__pwaInstallPrompt);
      setCanInstall(true);
    }
    const onAvailable = () => {
      setPrompt(window.__pwaInstallPrompt);
      setCanInstall(true);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setPrompt(null);
    };
    window.addEventListener('pwaInstallAvailable', onAvailable);
    window.addEventListener('pwaInstalled', onInstalled);
    return () => {
      window.removeEventListener('pwaInstallAvailable', onAvailable);
      window.removeEventListener('pwaInstalled', onInstalled);
    };
  }, []);

  /* ── Service-worker update detection ─────────────────────── */
  useEffect(() => {
    // Only run in production — SW is not registered in development
    if (process.env.NODE_ENV !== 'production') return;
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then((reg) => {
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(nw);
            setUpdateAvailable(true);
          }
        });
      });
    });

    // If a waiting worker already exists at mount time
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg?.waiting) {
        setWaitingWorker(reg.waiting);
        setUpdateAvailable(true);
      }
    });
  }, []);

  /* ── Trigger native install prompt ───────────────────────── */
  const triggerInstall = useCallback(async () => {
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setCanInstall(false);
      setPrompt(null);
      window.__pwaInstallPrompt = null;
      return true;
    }
    return false;
  }, [prompt]);

  /* ── Apply waiting service-worker update ─────────────────── */
  const applyUpdate = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      waitingWorker.addEventListener('statechange', () => {
        if (waitingWorker.state === 'activated') window.location.reload();
      });
    } else {
      window.location.reload();
    }
  }, [waitingWorker]);

  return {
    canInstall,
    isInstalled,
    isIOS,
    isAndroid,
    isMobile,
    browser,
    triggerInstall,
    updateAvailable,
    applyUpdate,
  };
}
